import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const StatusResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { application } = location.state || {};

  if (!application) {
    navigate('/check-status');
    return null;
  }

  const getStatusBadge = (status, customStatus) => {
    const displayStatus = customStatus || status;
    switch(status) {
      case 'approved':
        return <span className="status-badge approved">✅ {displayStatus}</span>;
      case 'rejected':
        return <span className="status-badge rejected">❌ {displayStatus}</span>;
      default:
        return <span className="status-badge pending">⏳ {displayStatus}</span>;
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved':
        return '🎉';
      case 'rejected':
        return '😞';
      case 'processing':
        return '🔄';
      case 'verified':
        return '✓';
      case 'disbursed':
        return '💰';
      case 'on-hold':
        return '⏸️';
      case 'cancelled':
        return '❌';
      default:
        return '⏳';
    }
  };

  const getStatusMessage = (application) => {
    const status = application.status;
    const customMessage = application.customMessage;
    
    // If there's a custom message from admin, show it first
    if (customMessage) {
      return customMessage;
    }
    
    // Default messages based on status
    switch(status) {
      case 'approved':
        return '🎉 Congratulations! Your loan has been approved. Our representative will contact you soon.';
      case 'rejected':
        return '❌ Unfortunately, your loan application has been rejected. Please contact support for more information.';
      case 'processing':
        return '🔄 Your application is being processed. We will update you soon.';
      case 'verified':
        return '✓ Your documents have been verified. Final approval in progress.';
      case 'disbursed':
        return '💰 Your loan amount has been disbursed to your bank account.';
      case 'on-hold':
        return '⏸️ Your application is on hold. Please contact support for more information.';
      case 'cancelled':
        return '❌ Your application has been cancelled as per your request.';
      default:
        return '⏳ Your application is under review. Our representative will review and contact you within 48 hours.';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved':
        return '#48bb78';
      case 'rejected':
        return '#f56565';
      case 'processing':
        return '#ed8936';
      case 'verified':
        return '#4299e1';
      case 'disbursed':
        return '#38a169';
      case 'on-hold':
        return '#ecc94b';
      case 'cancelled':
        return '#a0aec0';
      default:
        return '#ed8936';
    }
  };

  const calculateEMI = (amount, tenure, rate) => {
    const principal = parseFloat(amount);
    const ratePerMonth = (rate || 6) / (12 * 100);
    const months = parseFloat(tenure);
    const emi = principal * ratePerMonth * Math.pow(1 + ratePerMonth, months) / (Math.pow(1 + ratePerMonth, months) - 1);
    return isNaN(emi) ? 0 : emi;
  };

  const emi = calculateEMI(application.loanAmount, application.tenure, application.interestRate || 6);
  const displayStatus = application.customStatus || application.status;
  const statusIcon = getStatusIcon(application.status);

  return (
    <div className="status-result-container">
      <div className="status-result-card">
        <div className="status-header">
          <h2>Loan Application Status</h2>
          {getStatusBadge(application.status, application.customStatus)}
        </div>
        
        {/* Custom Status Message Section */}
        {application.customMessage && (
          <div className="custom-status-section" style={{ 
            background: '#fef5e7', 
            padding: '15px', 
            borderRadius: '12px', 
            marginBottom: '20px',
            borderLeft: `4px solid ${getStatusColor(application.status)}`
          }}>
            <div className="custom-status-icon" style={{ fontSize: '24px', marginBottom: '10px' }}>
              {statusIcon}
            </div>
            <p style={{ fontSize: '16px', fontWeight: '500', color: '#2d3748' }}>
             <strong>Recent Status:</strong>  {application.customMessage}
            </p>
            {application.adminRemark && (
              <p style={{ fontSize: '13px', color: '#718096', marginTop: '10px' }}>
                <strong>Note from Admin:</strong> {application.adminRemark}
              </p>
            )}
          </div>
        )}
        
        

        {/* Status Timeline */}
        <div className="status-timeline">
          <h3>Application Timeline</h3>
          <div className="timeline-items">
            <div className="timeline-item">
              <div className="timeline-dot completed"></div>
              <div className="timeline-content">
                <strong>Application Submitted</strong>
                <span>{new Date(application.createdAt).toLocaleString()}</span>
              </div>
            </div>
            {application.reviewedAt && (
              <div className="timeline-item">
                <div className={`timeline-dot ${application.status === 'approved' ? 'completed' : application.status === 'rejected' ? 'rejected' : 'pending'}`}></div>
                <div className="timeline-content">
                  <strong>Application Reviewed</strong>
                  <span>{new Date(application.reviewedAt).toLocaleString()}</span>
                  {application.reviewedBy && <small>By: {application.reviewedBy}</small>}
                </div>
              </div>
            )}
            {application.status === 'disbursed' && (
              <div className="timeline-item">
                <div className="timeline-dot completed"></div>
                <div className="timeline-content">
                  <strong>Amount Disbursed</strong>
                  <span>{new Date(application.updatedAt).toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="status-details">
          <h3>Application Details</h3>
          <div className="details-grid">
            <div className="detail-item">
              <label>Application ID</label>
              <p>{application.applicationId}</p>
            </div>
            <div className="detail-item">
              <label>Applied On</label>
              <p>{new Date(application.createdAt).toLocaleDateString()}</p>
            </div>
            <div className="detail-item">
              <label>Name</label>
              <p>{application.name}</p>
            </div>
            <div className="detail-item">
              <label>Mobile</label>
              <p>{application.mobile}</p>
            </div>
            <div className="detail-item">
              <label>Loan Amount</label>
              <p>₹{parseInt(application.loanAmount).toLocaleString('en-IN')}</p>
            </div>
            <div className="detail-item">
              <label>Tenure</label>
              <p>{application.tenure} months</p>
            </div>
            <div className="detail-item">
              <label>Interest Rate</label>
              <p>{application.interestRate || 6}% p.a.</p>
            </div>
            <div className="detail-item">
              <label>Monthly EMI</label>
              <p>₹{emi.toFixed(2)}</p>
            </div>
            <div className="detail-item">
              <label>Loan Purpose</label>
              <p>{application.loanPurpose}</p>
            </div>
            <div className="detail-item">
              <label>Employment Type</label>
              <p>{application.employmentType}</p>
            </div>
          </div>
        </div>

        {application.adminRemark && !application.customMessage && (
          <div className="admin-remark-section">
            <h3>Admin Remarks</h3>
            <p>{application.adminRemark}</p>
            {application.reviewedBy && (
              <small>Reviewed by: {application.reviewedBy} on {new Date(application.reviewedAt).toLocaleString()}</small>
            )}
          </div>
        )}

        <div className="action-buttons">
          <button onClick={() => navigate('/')} className="home-btn">
            Go to Home
          </button>
          <button onClick={() => navigate('/check-status')} className="apply-btn">
            Check Another Status
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusResult;